import React, { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { auth, db } from '../firebase';
import { useOrganization } from '../context/OrganizationContext';
import './PaymentStatusBanner.css';

// Estados de cobro en problemas. En org solo 'vencido' (lo setea
// invoice.payment_failed; el corte final va por 'canceled', fuera de esta lista,
// y ahí el paywall existente toma el control — no el banner). En personal se
// leen los estados legacy de estadoPago.
const ORG_OVERDUE = ['vencido'];
const PERSONAL_OVERDUE = ['vencido', 'fallido', 'impago'];

/**
 * Banner global sticky y NO descartable para cobro fallido. Vive fuera de
 * /billing (montado en App.js), así que lleva su propio onSnapshot sobre el doc
 * de la org (o del cliente en modo personal) — mismo principio reactivo que
 * Billing.jsx. Cuando el cliente actualiza la tarjeta y Stripe cobra,
 * invoice.payment_succeeded escribe billing.status:'active' y el snapshot apaga
 * el banner al instante, sin recarga.
 */
export default function PaymentStatusBanner() {
  const { context, loading } = useOrganization();
  const [overdue, setOverdue] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation('common');

  useEffect(() => {
    if (loading) return;

    let unsubSnapshot = null;

    const unsubAuth = onAuthStateChanged(auth, (u) => {
      if (unsubSnapshot) { unsubSnapshot(); unsubSnapshot = null; }
      if (!u) { setOverdue(false); return; }

      const isOrg = context.mode === 'organization' && context.orgId;
      const docRef = isOrg
        ? doc(db, 'organizations', context.orgId)
        : doc(db, 'clientes', u.uid);
      const overdueStates = isOrg ? ORG_OVERDUE : PERSONAL_OVERDUE;

      unsubSnapshot = onSnapshot(docRef, (snap) => {
        const data = snap.data() || {};
        const status = isOrg ? data.billing?.status : data.estadoPago;
        setOverdue(overdueStates.includes(status));
      }, () => setOverdue(false));
    });

    return () => {
      unsubAuth && unsubAuth();
      unsubSnapshot && unsubSnapshot();
    };
  }, [loading, context.mode, context.orgId]);

  if (!overdue) return null;

  return (
    <div className="payment-status-banner" role="alert">
      <span className="payment-status-banner__text">{t('paymentBanner.overdue')}</span>
      <button
        className="payment-status-banner__btn"
        onClick={() => navigate('/billing')}
      >
        {t('paymentBanner.updateCard')}
      </button>
    </div>
  );
}
